import { fg } from '@confluence/feature-gating';

import {
	ROVO_SMART_CREATION_GENERATED_BY,
	type AgentTrustReceipt,
	type AgentTrustReceiptActor,
	type AgentTrustReceiptPageData,
	type AgentTrustReceiptScenario,
	type AgentTrustReceiptScenarioId,
} from './agentTrustReceiptModel.ts';

const CREATED_BY_AI_LABEL = 'Created with Rovo';
const EDITED_BY_AI_LABEL = 'Edited with Rovo';
const READ_BY_AI_LABEL = 'Viewed by Rovo';
const SOURCE_LABEL = 'Source';
const EVIDENCE_LABEL = 'Evidence';
const PERSISTENCE_LABEL = 'Persistence';
const NETWORK_CALLS_LABEL = 'Network calls';
const CREATOR_LABEL = 'Creator';
const VERSION_LABEL = 'Version';
const UNKNOWN_CREATOR_NAME = 'the page creator';
const REAL_DATA_SOURCE_VALUE = 'Page content property (generatedBy)';
const REAL_DATA_NONE_VALUE = 'None (read from existing page data)';

const SPARK_RECEIPT_SCENARIOS: Record<AgentTrustReceiptScenarioId, AgentTrustReceiptScenario> = {
	CREATED_BY_AI: {
		id: 'CREATED_BY_AI',
		label: CREATED_BY_AI_LABEL,
		description: 'Spark preview data indicates Rovo created this page.',
	},
	EDITED_BY_AI: {
		id: 'EDITED_BY_AI',
		label: EDITED_BY_AI_LABEL,
		description: 'Spark preview data indicates Rovo helped edit this page.',
	},
	READ_BY_AI: {
		id: 'READ_BY_AI',
		label: READ_BY_AI_LABEL,
		description: 'Spark preview data indicates Rovo read this page as context.',
	},
};

const ROVO_AGENT_ACTOR: AgentTrustReceiptActor = {
	id: 'rovo-agent-spark-preview',
	name: 'Rovo agent',
	type: 'agent',
	agentType: 'rovo',
	iconKey: 'rovo-agent',
};

const SPACE_ORGANIZER_ACTOR: AgentTrustReceiptActor = {
	id: 'space-organizer-agent-spark-preview',
	name: 'Space Organizer Agent',
	type: 'agent',
	agentType: 'space-organizer',
	iconKey: 'space-organizer-agent',
};

const DEV_DOCS_ACTOR: AgentTrustReceiptActor = {
	id: 'dev-docs-agent-spark-preview',
	name: 'Dev Docs Agent',
	type: 'agent',
	agentType: 'dev-docs',
	iconKey: 'dev-docs-agent',
};

// Fabricated demo receipts for pages NOT created by Rovo Smart Create. CREATED_BY_AI is deliberately
// excluded so the "Created with Rovo" label is only ever shown from real page data — never fabricated
// onto a page that did not return generatedBy === 'ROVO_SMART_CREATION'.
type FabricatedBucket = {
	scenarioId: AgentTrustReceiptScenarioId;
	actor: AgentTrustReceiptActor;
};

const FABRICATED_BUCKETS: FabricatedBucket[] = [
	{ scenarioId: 'EDITED_BY_AI', actor: SPACE_ORGANIZER_ACTOR },
	{ scenarioId: 'READ_BY_AI', actor: DEV_DOCS_ACTOR },
];

const isValidContentId = (contentId: string) => /^[1-9]\d*$/.test(contentId);

const resolveFabricatedBucket = (contentId: string): number => {
	const lastDigit = Number(contentId.charAt(contentId.length - 1));
	return lastDigit % FABRICATED_BUCKETS.length;
};

const createSparkPreviewSignals = (
	scenario: AgentTrustReceiptScenario,
): AgentTrustReceipt['signals'] => [
	{
		id: 'source',
		label: SOURCE_LABEL,
		value: 'Spark preview fixture',
	},
	{
		id: 'evidence',
		label: EVIDENCE_LABEL,
		value: `Example evidence for ${scenario.label.toLowerCase()}`,
	},
	{
		id: 'persistence',
		label: PERSISTENCE_LABEL,
		value: 'None',
	},
	{
		id: 'network',
		label: NETWORK_CALLS_LABEL,
		value: 'None',
	},
];

const createSparkPreviewEvents = (
	actor: AgentTrustReceiptActor,
	scenario: AgentTrustReceiptScenario,
): AgentTrustReceipt['events'] =>
	[
		{
			id: 'context-reviewed',
			occurredAt: '2026-06-03T21:00:00.000Z',
			title: 'Context reviewed',
			description: `${actor.name} inspected example page context for ${scenario.label.toLowerCase()}.`,
		},
		{
			id: 'agent-action',
			occurredAt: '2026-06-03T21:02:00.000Z',
			title: scenario.label,
			description: `${actor.name} performed the Spark preview activity recorded in this receipt.`,
		},
		{
			id: 'receipt-created',
			occurredAt: '2026-06-03T21:04:00.000Z',
			title: 'Receipt prepared',
			description: 'Example evidence was collected for review in this Spark preview receipt.',
		},
	].sort((a, b) => a.occurredAt.localeCompare(b.occurredAt));

// Builds a CREATED_BY_AI receipt backed by REAL page data (creator, created/updated timestamps,
// version number) for pages confirmed to be created by Rovo Smart Create. Events with a missing
// timestamp are omitted so the panel never renders an Invalid Date.
const createRealCreatedByAiReceipt = (
	contentId: string,
	pageData: AgentTrustReceiptPageData,
): AgentTrustReceipt => {
	const scenario = SPARK_RECEIPT_SCENARIOS.CREATED_BY_AI;
	const creatorName = pageData.creatorName?.trim() || UNKNOWN_CREATOR_NAME;
	const versionDetail =
		typeof pageData.versionNumber === 'number'
			? `${VERSION_LABEL} ${pageData.versionNumber}`
			: null;

	const events: AgentTrustReceipt['events'] = [];
	if (pageData.createdAt) {
		events.push({
			id: 'page-created',
			occurredAt: pageData.createdAt,
			title: scenario.label,
			description: `${creatorName} created this page with Rovo.`,
		});
	}
	if (pageData.lastUpdatedAt) {
		events.push({
			id: 'page-last-updated',
			occurredAt: pageData.lastUpdatedAt,
			title: 'Last updated',
			description: versionDetail
				? `Latest published change (${versionDetail.toLowerCase()}).`
				: 'Latest published change.',
		});
	}
	events.sort((a, b) => a.occurredAt.localeCompare(b.occurredAt));

	const signals: AgentTrustReceipt['signals'] = [
		{
			id: 'source',
			label: SOURCE_LABEL,
			value: REAL_DATA_SOURCE_VALUE,
		},
		{
			id: 'creator',
			label: CREATOR_LABEL,
			value: creatorName,
		},
		{
			id: 'version',
			label: VERSION_LABEL,
			value: versionDetail ?? 'Unknown',
		},
		{
			id: 'persistence',
			label: PERSISTENCE_LABEL,
			value: REAL_DATA_NONE_VALUE,
		},
		{
			id: 'network',
			label: NETWORK_CALLS_LABEL,
			value: REAL_DATA_NONE_VALUE,
		},
	];

	return {
		contentId,
		actor: ROVO_AGENT_ACTOR,
		scenario,
		// CREATED_BY_AI receipts are sourced from real page data, so they must NOT claim "no audit log
		// was created" — that copy only applies to fabricated Spark preview receipts.
		summary: `Created with Rovo by ${creatorName}. This receipt is sourced from existing page data.`,
		events,
		signals,
	};
};

export const resolveAgentTrustReceipt = (
	contentId: string,
	pageData?: AgentTrustReceiptPageData,
): AgentTrustReceipt | null => {
	if (!fg('cc_sparks_agent_trust_receipt')) {
		return null;
	}

	const normalizedContentId = contentId.trim();

	if (!isValidContentId(normalizedContentId)) {
		return null;
	}

	// Real CREATED_BY_AI data only when the page is positively confirmed as Rovo Smart Create.
	// Exact-literal match excludes null, ONBOARDING, and AI_PLANNER.
	if (pageData?.generatedBy === ROVO_SMART_CREATION_GENERATED_BY) {
		return createRealCreatedByAiReceipt(normalizedContentId, pageData);
	}

	// Non-Rovo pages fall back to fabricated EDITED_BY_AI / READ_BY_AI demo receipts only — never
	// CREATED_BY_AI, so "Created with Rovo" is never asserted on a page Rovo did not create.
	const { scenarioId, actor } = FABRICATED_BUCKETS[resolveFabricatedBucket(normalizedContentId)];
	const scenario = SPARK_RECEIPT_SCENARIOS[scenarioId];

	return {
		contentId: normalizedContentId,
		actor,
		scenario,
		summary: `${scenario.description} This receipt identifies ${actor.name}, the recorded agent activity, and the chronological events available for review. Spark preview data only, not a durable audit log.`,
		events: createSparkPreviewEvents(actor, scenario),
		signals: createSparkPreviewSignals(scenario),
	};
};
